import { useTranslation } from "@localization/useTranslation";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { getUniqueFilterOptions, sortByEnumValue, sortByName } from "@common/hooks/filterUtils.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { exhaustiveGuard } from "@common/utilities";
import { DataPrivacyClassification } from "../../DataCategoriesStep/DataCategoriesStep.types";
import { LegalBasesDialogViewModel } from "../ProcessingActivityOverview.types";
import { IProcessingActivityDataCategoriesTab } from "./ProcessingActivityDataCategoriesTab";
import { useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";

export interface ProcessingActivityDataCategoryTableRow {
    rowId: string;
    dataCategoryId: string;
    name: string;
    classificationId: string;
    classification: string;
    purpose: string;
    legalBasis: string;
    doubleLegalBasis: string;
    nationalLaw: string;
    deletionPeriod: string;
}

export default function useProcessingActivityDataCategoriesTab(props: IProcessingActivityDataCategoriesTab) {
    const { translateString } = useTranslation();
    const { isLoading, data } = useDotLegalQueryWithKey<Array<LegalBasesDialogViewModel>>(
        "processingActivityDataCategories" + props.processingActivityId,
        `/ProcessingActivityOverview/${props.processingActivityId}/data-categories`
    );
    const legalBases = data ?? [];
    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `processingActivity-${props.processingActivityId}-dataCategories-filters`
    );

    function getClassificationName(classification: DataPrivacyClassification) {
        switch (classification) {
            case DataPrivacyClassification.None:
                return translateString("dataCategoriNone");
            case DataPrivacyClassification.Ordinary:
                return translateString("dataCategoriOrdinary");
            case DataPrivacyClassification.Sensitive:
                return translateString("dataCategoriSensitive");
            case DataPrivacyClassification.NationalIdentificationNumber:
                return translateString("nationalidentificationnumber");
            case DataPrivacyClassification.CriminalOffenses:
                return translateString("criminaloffenses");
            default:
                return exhaustiveGuard(classification);
        }
    }

    // A data category with purpose specific legal bases has one set of legal bases per purpose, so it gets a row per purpose.
    function toRows(dataCategory: LegalBasesDialogViewModel): Array<ProcessingActivityDataCategoryTableRow> {
        return dataCategory.dataCategoryViewItems.map((legalBases, index) => ({
            rowId: `${dataCategory.id}-${index}`,
            dataCategoryId: dataCategory.id,
            name: dataCategory.name,
            classificationId: dataCategory.classification.toString(),
            classification: getClassificationName(dataCategory.classification),
            purpose: legalBases.purpose,
            legalBasis: legalBases.directive,
            doubleLegalBasis: legalBases.doubleBasis,
            nationalLaw: legalBases.nationalLaw,
            deletionPeriod: legalBases.deletionPeriod,
        }));
    }

    const allRows = legalBases.flatMap(toRows);
    const hasPurposeSpecificLegalBases = legalBases.some((dataCategory) => dataCategory.dataCategoryViewItems.length > 1);

    const legalBasisColumns: Array<{ id: keyof ProcessingActivityDataCategoryTableRow; label: string }> = [
        { id: "legalBasis", label: translateString("legalBasis") },
        { id: "doubleLegalBasis", label: translateString("doubleLegalBasis") },
        { id: "nationalLaw", label: translateString("nationalLaw") },
        { id: "deletionPeriod", label: translateString("deletionPeriod") },
    ];
    const textColumns = hasPurposeSpecificLegalBases
        ? [{ id: "purpose" as const, label: translateString("purpose") }, ...legalBasisColumns]
        : legalBasisColumns;

    const rows = applyClientFilters(
        allRows,
        [
            { id: "dataCategory", candidate: (row) => row.dataCategoryId },
            { id: "classification", candidate: (row) => row.classificationId },
            ...textColumns.map((column) => ({ id: column.id, candidate: (row: ProcessingActivityDataCategoryTableRow) => row[column.id] })),
        ],
        selectFilters,
        {},
        filterOperators
    );

    // These columns hold names rather than ids, so the name doubles as the filter id.
    function getTextColumnFilterOption(column: { id: keyof ProcessingActivityDataCategoryTableRow; label: string }): FilterOption {
        return {
            id: column.id,
            label: column.label,
            type: FilterOptionType.SelectField,
            value: selectFilters[column.id] ?? [],
            fixedData: getUniqueFilterOptions(allRows, (row) => row[column.id]).sort(sortByName),
        };
    }

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "dataCategory",
                    label: translateString("dataCategory"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.dataCategory ?? [],
                    fixedData: getUniqueFilterOptions(
                        allRows,
                        (row) => row.name,
                        (row) => row.dataCategoryId
                    ).sort(sortByName),
                },
                {
                    id: "classification",
                    label: translateString("classification"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.classification ?? [],
                    fixedData: getUniqueFilterOptions(
                        allRows,
                        (row) => row.classification,
                        (row) => row.classificationId
                    ).sort(sortByEnumValue),
                },
                ...textColumns.map(getTextColumnFilterOption),
            ],
            filterOperators
        );
    }

    const hasActiveFilters = Object.values(selectFilters).some((v) => v.length > 0);

    return {
        rows: rows ?? [],
        isLoading,
        hasData: isLoading || allRows.length > 0,
        textColumns,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
    };
}
